import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Loader2, Search, ChevronDown, FileText, FileSpreadsheet, CalendarDays } from 'lucide-react';
import jsPDF from 'jspdf';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import TabPageLayout, { DataCard } from './TabPageLayout';
import DataTable from './DataTable';
import StatusBadge from './StatusBadge';
import { BACKEND_URL } from '../config/api';
import { logAdminEvent, formatTripDate, formatTripDateShort } from '../utils/superAdmin';

const PLATFORM_API = `${BACKEND_URL}/api/v2/super-admin`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

const formatCurrency = (amount, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(Number(amount) || 0);

const firstOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
};
const today = () => new Date().toISOString().split('T')[0];

const PAGE_SIZE = 50;

const SuperAdminBookingsTab = () => {
  const [startDate, setStartDate] = useState(firstOfMonth());
  const [endDate, setEndDate] = useState(today());
  const [orgFilter, setOrgFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);

  const [orgs, setOrgs] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${PLATFORM_API}/organizations`, authHeaders())
      .then(res => setOrgs((res.data.organizations || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))))
      .catch(() => {});
  }, []);

  // Auto-search as you type (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(prev => {
        const next = searchInput.trim();
        if (next !== prev) setPage(0);
        return next;
      });
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      if (orgFilter !== 'all') params.set('org_id', orgFilter);
      if (search) params.set('search', search);
      const res = await axios.get(`${PLATFORM_API}/bookings?${params}`, authHeaders());
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, orgFilter, search, page]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const applyPreset = (preset) => {
    const now = new Date();
    setPage(0);
    if (preset === 'thisMonth') { setStartDate(firstOfMonth()); setEndDate(today()); }
    else if (preset === 'last30') { setStartDate(new Date(now.getTime() - 30 * 86400000).toISOString().split('T')[0]); setEndDate(today()); }
    else if (preset === 'last7') { setStartDate(new Date(now.getTime() - 7 * 86400000).toISOString().split('T')[0]); setEndDate(today()); }
    else if (preset === 'thisYear') { setStartDate(new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0]); setEndDate(today()); }
  };

  const bookings = data?.bookings || [];
  const summary = data?.summary || {};
  const totalPages = Math.max(1, Math.ceil((summary.total_count || 0) / PAGE_SIZE));

  const exportCSV = () => {
    if (!bookings.length) { toast.error('No bookings to export'); return; }
    const rows = [
      ['BOOKINGFLOWS PLATFORM BOOKINGS'],
      [`Period: ${startDate} to ${endDate}`],
      [`Total: ${summary.total_count} bookings | Paid GMV: ${(summary.paid_gmv || 0).toFixed(2)} | Platform Fees: ${(summary.paid_fees || 0).toFixed(2)}`],
      [''],
      ['Code', 'Company', 'Service', 'Trip Date', 'Currency', 'Total', 'Platform Fee', 'Status', 'Payment'],
      ...bookings.map(b => [
        b.code, `"${b.org_name}"`, `"${b.service_name || ''}"`, b.booking_date, b.currency,
        b.total_price.toFixed(2), b.platform_fee.toFixed(2), b.status, b.payment_status
      ]),
    ];
    const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    logAdminEvent('report_exported', { report: 'platform_bookings', format: 'csv' });
    toast.success('Bookings exported');
  };

  const exportPDF = () => {
    if (!bookings.length) { toast.error('No bookings to export'); return; }
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('BookingFlows — Platform Bookings', pageWidth / 2, y, { align: 'center' });
    y += 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    const currencyLine = (summary.by_currency?.length > 0)
      ? summary.by_currency.map(c => `${c.currency}: GMV ${formatCurrency(c.paid_gmv, c.currency)} / fees ${formatCurrency(c.paid_fees, c.currency)}`).join('   ')
      : `Paid GMV ${formatCurrency(summary.paid_gmv)}  •  Fees ${formatCurrency(summary.paid_fees)}`;
    doc.text(`${startDate} to ${endDate}  •  ${summary.total_count} bookings  •  ${currencyLine}`, pageWidth / 2, y, { align: 'center' });
    y += 12;

    doc.setTextColor(0);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    const cols = [{ h: 'Code', x: 14 }, { h: 'Company', x: 44 }, { h: 'Trip Date', x: 96 }, { h: 'Total', x: 124 }, { h: 'Fee', x: 148 }, { h: 'Payment', x: 168 }];
    cols.forEach(c => doc.text(c.h, c.x, y));
    y += 5;
    doc.setFont('helvetica', 'normal');

    bookings.forEach(b => {
      if (y > 280) { doc.addPage(); y = 20; }
      doc.text(String(b.code || '').substring(0, 14), 14, y);
      doc.text(String(b.org_name || '').substring(0, 26), 44, y);
      doc.text(formatTripDateShort(b.booking_date), 96, y);
      doc.text(formatCurrency(b.total_price, b.currency), 124, y);
      doc.text(formatCurrency(b.platform_fee, b.currency), 148, y);
      doc.text(String(b.payment_status || ''), 168, y);
      y += 5;
    });

    doc.setFontSize(7);
    doc.setTextColor(150);
    doc.text('BookingFlows — Confidential', 14, 290);
    doc.save(`bookings-${startDate}-to-${endDate}.pdf`);
    logAdminEvent('report_exported', { report: 'platform_bookings', format: 'pdf' });
    toast.success('PDF exported');
  };

  const bookingColumns = [
    { key: 'code', label: 'Code', render: (b) => <span className="font-mono text-xs text-gray-900">{b.code}</span> },
    { key: 'org_name', label: 'Company', render: (b) => <span className="font-medium text-gray-900">{b.org_name}</span> },
    { key: 'service_name', label: 'Service', render: (b) => <span className="text-gray-600 block max-w-[180px] truncate">{b.service_name || '—'}</span> },
    { key: 'booking_date', label: 'Trip Date', render: (b) => <span className="text-gray-600 whitespace-nowrap">{formatTripDate(b.booking_date)}</span> },
    { key: 'total_price', label: 'Total', align: 'right', render: (b) => <span className="font-medium text-gray-900">{formatCurrency(b.total_price, b.currency)}</span> },
    { key: 'platform_fee', label: 'Platform Fee', align: 'right', render: (b) => <span className="text-gray-600">{formatCurrency(b.platform_fee, b.currency)}</span> },
    { key: 'payment_status', label: 'Payment', render: (b) => <StatusBadge status={b.payment_status} /> },
  ];

  return (
    <TabPageLayout
      title="Bookings"
      subtitle="Every booking across the platform"
      loading={loading && !data}
      actions={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9" data-testid="bookings-export-button">
              <Download className="w-4 h-4 mr-1" />Export<ChevronDown className="w-3 h-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={exportPDF} data-testid="bookings-export-pdf">
              <FileText className="w-4 h-4 mr-2" />PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exportCSV} data-testid="bookings-export-csv">
              <FileSpreadsheet className="w-4 h-4 mr-2" />CSV
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      <div className="space-y-6" data-testid="superadmin-bookings-tab">
        <DataCard>
          {/* Filters */}
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">From</Label>
              <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(0); }} className="h-9 w-40" data-testid="bookings-start-date" />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">To</Label>
              <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(0); }} className="h-9 w-40" data-testid="bookings-end-date" />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Company</Label>
              <Select value={orgFilter} onValueChange={(v) => { setOrgFilter(v); setPage(0); }}>
                <SelectTrigger className="h-9 w-52" data-testid="bookings-org-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All companies</SelectItem>
                  {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="relative">
              <Label className="text-xs text-gray-500 mb-1 block">Search</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  className="h-9 w-48 pl-9"
                  placeholder="Code or company..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(0); } }}
                  data-testid="bookings-search-input"
                />
              </div>
            </div>
            <div className="flex gap-2 pb-0.5">
              {[
                { key: 'last7', label: '7 Days' },
                { key: 'thisMonth', label: 'This Month' },
                { key: 'last30', label: '30 Days' },
                { key: 'thisYear', label: 'This Year' },
              ].map(p => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p.key)}
                  className="px-3 py-1.5 text-xs font-medium rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                  data-testid={`bookings-preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Summary line */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-x-8 gap-y-1 text-sm text-gray-600" data-testid="bookings-summary">
            <span><span className="font-semibold text-gray-900">{(summary.total_count || 0).toLocaleString()}</span> bookings</span>
            {(summary.by_currency?.length > 0) ? (
              summary.by_currency.map((c) => (
                <span key={c.currency} data-testid={`bookings-summary-${c.currency}`}>
                  <span className="font-medium text-gray-500">{c.currency}</span>
                  {' '}Paid GMV <span className="font-semibold text-gray-900">{formatCurrency(c.paid_gmv, c.currency)}</span>
                  {' · '}Fees <span className="font-semibold text-gray-900">{formatCurrency(c.paid_fees, c.currency)}</span>
                </span>
              ))
            ) : (
              <>
                <span>Paid GMV <span className="font-semibold text-gray-900">{formatCurrency(summary.paid_gmv)}</span></span>
                <span>Platform fees <span className="font-semibold text-gray-900">{formatCurrency(summary.paid_fees)}</span></span>
              </>
            )}
          </div>
        </DataCard>

        <DataCard>
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <CalendarDays className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No bookings for this period</p>
            </div>
          ) : (
            <>
              <DataTable
                columns={bookingColumns}
                rows={bookings}
                rowKey="id"
                minWidth={760}
                testId="bookings-table"
              />

              {/* Pagination */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-2">
                <p className="text-xs text-gray-500">
                  Page {page + 1} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="h-8" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="bookings-prev-page">Previous</Button>
                  <Button variant="outline" size="sm" className="h-8" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="bookings-next-page">Next</Button>
                </div>
              </div>
            </>
          )}
        </DataCard>
      </div>
    </TabPageLayout>
  );
};

export default SuperAdminBookingsTab;
